import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import {
  AtSign, ChevronDown, ChevronRight, Copy, Download, FileText, Globe2, Loader2,
  PieChart, Scissors, Search, ShieldAlert,
} from 'lucide-react';
import { api } from '../api/client.js';
import {
  EMAIL_RE, SEPARATOR_RE, classify, parseBlacklist, DROP_EMPTY, DROP_INVALID, DROP_BLACKLISTED,
} from './emailCleanerCore.js';

/**
 * /tools — 4 standalone utilities (BUILD-QUEUE §5, tapi replacement).
 * Only the DNS checker talks to the backend; the other three are pure
 * client-side — nothing leaves the browser.
 */
export function Tools() {
  const [active, setActive] = useState('dns');

  const TABS = [
    { key: 'dns', label: 'DNS Record Checker', icon: Search },
    { key: 'blacklist', label: 'Blacklist Checker', icon: ShieldAlert },
    { key: 'cleaner', label: 'Email List Cleaner', icon: AtSign },
    { key: 'splitter', label: 'File Splitter', icon: Scissors },
    { key: 'grouper', label: 'Email Domain Grouper', icon: PieChart },
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-t3">
          Standalone utilities. Email/file tools run entirely in your browser — nothing is uploaded.
        </p>
      </div>

      <div className="flex gap-1 flex-wrap border-b border-bd pb-px">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActive(t.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-t-md border-b-2 ${
              active === t.key
                ? 'border-blue text-blue font-semibold'
                : 'border-transparent text-t3 hover:text-t1'
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {active === 'dns' && <DnsChecker />}
      {active === 'blacklist' && <BlacklistChecker />}
      {active === 'cleaner' && <EmailCleaner />}
      {active === 'splitter' && <FileSplitter />}
      {active === 'grouper' && <DomainGrouper />}
    </div>
  );
}

// ─── shared helpers ─────────────────────────────────────────────────────────

function parseLines(text) {
  return text.split(/[\r\n,;]+/).map((s) => s.trim()).filter(Boolean);
}

function downloadText(filename, content, type = 'text/plain') {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function FilePicker({ onText, accept = '.txt,.csv' }) {
  const ref = useRef(null);
  return (
    <>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2"
      >
        <FileText className="w-3.5 h-3.5" /> Load from file
      </button>
      <input
        ref={ref}
        type="file"
        accept={accept}
        className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) onText(await f.text(), f.name);
          e.target.value = '';
        }}
      />
    </>
  );
}

const cardCls = 'bg-white border border-bd rounded-lg p-5 space-y-4';
const textareaCls = 'w-full h-40 rounded-md border border-bd-strong px-3 py-2 text-sm font-mono';
const btnPrimary = 'inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50';
const btnGhost = 'inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-bd-strong text-t2 hover:bg-s-2 disabled:opacity-50';

// ─── 1) DNS Record Checker (backend: node dns.promises) ────────────────────

const DNS_TYPES = ['A', 'AAAA', 'MX', 'TXT', 'NS', 'CNAME'];
const EMAIL_CHECKS = [
  { key: 'spf', label: 'SPF' },
  { key: 'dmarc', label: 'DMARC' },
  { key: 'dkim', label: 'DKIM' },
  { key: 'ips', label: 'Sending IPs' },
];

function csvCell(v) {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function DnsChecker() {
  const [input, setInput] = useState('');
  const [types, setTypes] = useState(['A', 'MX']);
  const [emailChecks, setEmailChecks] = useState([]);
  const [selectorInput, setSelectorInput] = useState('');
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [copied, setCopied] = useState(false);

  const domains = parseLines(input);
  const customSelectors = parseLines(selectorInput).map((s) => s.toLowerCase());

  function toggle(list, setList, key) {
    setList((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  }

  async function run() {
    setRunning(true);
    setError(null);
    setData(null);
    setExpanded(new Set());
    try {
      const resp = await api('/api/tools/dns-check', {
        method: 'POST',
        body: { domains, types, emailChecks, dkimSelectors: customSelectors },
      });
      setData(resp);
    } catch (err) {
      setError(err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message);
    } finally {
      setRunning(false);
    }
  }

  function exportCsv() {
    if (!data) return;
    const cols = ['domain', ...data.types];
    if (data.emailChecks?.includes('spf')) cols.push('SPF', 'SPF_problems');
    if (data.emailChecks?.includes('dmarc')) cols.push('DMARC');
    if (data.emailChecks?.includes('dkim')) cols.push('DKIM_selectors');
    if (data.emailChecks?.includes('ips')) cols.push('Sending_IPs');
    const rows = data.results.map((r) => {
      const cells = [r.domain];
      for (const t of data.types) {
        const rec = r.records[t];
        cells.push(rec?.error ? `err:${rec.error}` : rec?.found ? `yes(${rec.count})` : 'no');
      }
      const e = r.email || {};
      if (data.emailChecks?.includes('spf')) {
        cells.push(e.spf?.found ? e.spf.policy : 'missing');
        cells.push(e.spf?.problems?.join(' | ') || '');
      }
      if (data.emailChecks?.includes('dmarc')) cells.push(e.dmarc?.found ? `p=${e.dmarc.policy}` : 'missing');
      if (data.emailChecks?.includes('dkim')) cells.push(e.dkim?.found ? e.dkim.selectors.join(' ') : 'none found');
      if (data.emailChecks?.includes('ips')) cells.push(e.ips?.found ? (e.ips.ips || []).join(' ') : 'no spf');
      return cells;
    });
    const csv = [cols, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
    downloadText('dns-check.csv', csv, 'text/csv');
  }

  async function copyDomains() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.results.map((r) => r.domain).join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — ignore */ }
  }

  function toggleRow(domain) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  }

  const activeEmail = data?.emailChecks ?? [];
  const hasEmail = activeEmail.length > 0;
  const nothingSelected = types.length === 0 && emailChecks.length === 0;

  return (
    <div className={cardCls}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-t2">
          Paste domains (one per line) and pick record types — resolves from the panel server.
        </p>
        <FilePicker onText={(t) => setInput((v) => (v ? `${v}\n${t}` : t))} />
      </div>
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={'example.com\nexample.org'}
        className={textareaCls}
      />

      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-medium uppercase tracking-wide text-t3 w-full sm:w-auto">Records</span>
          {DNS_TYPES.map((t) => (
            <label key={t} className="inline-flex items-center gap-1 text-sm">
              <input type="checkbox" checked={types.includes(t)} onChange={() => toggle(types, setTypes, t)} />
              <span className="font-mono">{t}</span>
            </label>
          ))}
        </div>

        <div className="flex items-start gap-3 flex-wrap">
          <span className="text-xs font-medium uppercase tracking-wide text-t3 w-full sm:w-auto sm:pt-0.5">
            Email auth
          </span>
          {EMAIL_CHECKS.map((c) => (
            <label key={c.key} className="inline-flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={emailChecks.includes(c.key)}
                onChange={() => toggle(emailChecks, setEmailChecks, c.key)}
              />
              <span>{c.label}</span>
            </label>
          ))}
          <span className="text-xs text-t3">extra lookups — off by default</span>
        </div>

        {emailChecks.includes('dkim') && (
          <label className="block max-w-lg">
            <span className="text-xs text-t2">
              Extra DKIM selectors (optional — applied to every domain; 19 common ones are always probed)
            </span>
            <input
              type="text"
              value={selectorInput}
              onChange={(e) => setSelectorInput(e.target.value)}
              placeholder="e.g. hs1, mycustom, myselector2"
              className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm font-mono"
            />
          </label>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" disabled={running || domains.length === 0 || nothingSelected} onClick={run} className={btnPrimary}>
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Check {domains.length} domain{domains.length === 1 ? '' : 's'}
        </button>
        <span className="text-xs text-t3">max 100 domains / request</span>
      </div>

      {error && <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">{error}</div>}

      {data && (
        <div className="space-y-2">
          {data.invalid_domains?.length > 0 && (
            <p className="text-xs text-warn">skipped invalid: {data.invalid_domains.join(', ')}</p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={exportCsv} className={btnGhost}>
              <Download className="w-4 h-4" /> Export as CSV
            </button>
            <button type="button" onClick={copyDomains} className={btnGhost}>
              <Copy className="w-4 h-4" /> {copied ? 'Copied!' : 'Copy domains'}
            </button>
            {hasEmail && <span className="text-xs text-t3">✗ under DKIM = no known selector found (not proof there's no DKIM)</span>}
          </div>
          <div className="overflow-x-auto border border-bd rounded-md">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                  {hasEmail && <th className="px-2 py-2 w-6" />}
                  <th className="px-3 py-2">Domain</th>
                  {data.types.map((t) => <th key={t} className="px-3 py-2 font-mono">{t}</th>)}
                  {activeEmail.includes('spf') && <th className="px-3 py-2">SPF</th>}
                  {activeEmail.includes('dmarc') && <th className="px-3 py-2">DMARC</th>}
                  {activeEmail.includes('dkim') && <th className="px-3 py-2">DKIM</th>}
                  {activeEmail.includes('ips') && <th className="px-3 py-2">Sending IPs</th>}
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => {
                  const e = r.email || {};
                  const isOpen = expanded.has(r.domain);
                  const colSpan = 1 + data.types.length + activeEmail.length + (hasEmail ? 1 : 0);
                  return (
                    <Fragment key={r.domain}>
                      <tr className="border-b border-bd">
                        {hasEmail && (
                          <td className="px-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => toggleRow(r.domain)}
                              className="text-t3 hover:text-t2"
                              title="Show details"
                            >
                              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                          </td>
                        )}
                        <td className="px-3 py-1.5 font-medium">{r.domain}</td>
                        {data.types.map((t) => {
                          const rec = r.records[t];
                          return (
                            <td key={t} className="px-3 py-1.5">
                              {rec?.error ? (
                                <span className="text-warn text-xs" title={rec.error}>err</span>
                              ) : rec?.found ? (
                                <span className="text-ok">✓ {rec.count > 1 ? rec.count : ''}</span>
                              ) : (
                                <span className="text-err">✗</span>
                              )}
                            </td>
                          );
                        })}
                        {activeEmail.includes('spf') && (
                          <td className="px-3 py-1.5">
                            {e.spf?.found ? (
                              <span className="inline-flex items-center gap-1">
                                <span className="text-ok">✓</span>
                                {e.spf.problems?.length > 0 && (
                                  <span className="text-warn text-xs" title={e.spf.problems.join('\n')}>
                                    ⚠ {e.spf.problems.length}
                                  </span>
                                )}
                              </span>
                            ) : <span className="text-err">✗</span>}
                          </td>
                        )}
                        {activeEmail.includes('dmarc') && (
                          <td className="px-3 py-1.5">
                            {e.dmarc?.found ? (
                              <span className="text-ok">✓ <span className="font-mono text-xs text-t2">p={e.dmarc.policy}</span></span>
                            ) : <span className="text-err">✗</span>}
                          </td>
                        )}
                        {activeEmail.includes('dkim') && (
                          <td className="px-3 py-1.5">
                            {e.dkim?.found ? (
                              <span className="text-ok" title={`selectors: ${e.dkim.selectors.join(', ')}`}>
                                ✓ <span className="font-mono text-xs text-t2">{e.dkim.selectors.slice(0, 2).join(', ')}{e.dkim.selectors.length > 2 ? '…' : ''}</span>
                              </span>
                            ) : <span className="text-err" title="no known selector found">✗</span>}
                          </td>
                        )}
                        {activeEmail.includes('ips') && (
                          <td className="px-3 py-1.5">
                            {e.ips?.found ? (
                              <span className="text-t2 text-xs">
                                {e.ips.count} IP{e.ips.count === 1 ? '' : 's'}
                                {e.ips.limitHit && <span className="text-warn" title="SPF 10-lookup limit reached — list may be partial"> ⚠</span>}
                              </span>
                            ) : <span className="text-err" title="no SPF record">✗</span>}
                          </td>
                        )}
                      </tr>
                      {hasEmail && isOpen && (
                        <tr className="border-b border-bd bg-s-2">
                          <td colSpan={colSpan} className="px-4 py-3">
                            <EmailDetail email={e} checks={activeEmail} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function EmailDetail({ email, checks }) {
  const e = email || {};
  return (
    <div className="space-y-2 text-xs">
      {checks.includes('spf') && (
        <div>
          <span className="font-semibold text-t2">SPF: </span>
          {e.spf?.found ? (
            <>
              <code className="font-mono text-t2 break-all">{e.spf.policy}</code>
              {e.spf.problems?.length > 0 && (
                <ul className="mt-1 ml-3 list-disc text-warn">
                  {e.spf.problems.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              )}
            </>
          ) : <span className="text-err">no SPF record on the apex</span>}
        </div>
      )}
      {checks.includes('dmarc') && (
        <div>
          <span className="font-semibold text-t2">DMARC: </span>
          {e.dmarc?.found
            ? <code className="font-mono text-t2 break-all">{e.dmarc.raw}</code>
            : <span className="text-err">no _dmarc TXT record</span>}
        </div>
      )}
      {checks.includes('dkim') && (
        <div>
          <span className="font-semibold text-t2">DKIM: </span>
          {e.dkim?.found
            ? <span className="text-t2">matched selector(s): <code className="font-mono">{e.dkim.selectors.join(', ')}</code></span>
            : <span className="text-err">no known selector matched (try adding the provider's selector above)</span>}
        </div>
      )}
      {checks.includes('ips') && (
        <div>
          <span className="font-semibold text-t2">Sending IPs (from SPF): </span>
          {e.ips?.found ? (
            e.ips.count > 0
              ? <code className="font-mono text-t2 break-all">{e.ips.ips.join(', ')}</code>
              : <span className="text-t3">SPF present but no static IPs (all/redirect only)</span>
          ) : <span className="text-err">no SPF record to expand</span>}
          {e.ips?.limitHit && <span className="text-warn"> — stopped at the 10-lookup limit, list may be incomplete</span>}
        </div>
      )}
    </div>
  );
}

// ─── 1b) Blacklist Checker (DNSBL/RBL — backend) ────────────────────────────

const BL_STATE = {
  clean:          { glyph: '✓', cls: 'bg-s-green text-ok border-bd', label: 'clean' },
  listed:         { glyph: '✗', cls: 'bg-err-bg text-err border-err-bd font-semibold', label: 'LISTED' },
  blocked:        { glyph: '⊘', cls: 'bg-s-3 text-warn border-bd', label: 'unavailable (resolver refused / needs key)' },
  error:          { glyph: '!', cls: 'bg-s-3 text-t3 border-bd', label: 'check failed' },
  not_applicable: { glyph: '–', cls: 'bg-s-2 text-ic-light border-bd', label: 'not applicable' },
};

function BlacklistChecker() {
  const [zones, setZones] = useState(null);
  const [zonesErr, setZonesErr] = useState(null);
  const [input, setInput] = useState('');
  const [resolver, setResolver] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let alive = true;
    api('/api/tools/blacklist-zones')
      .then((r) => { if (alive) setZones(r.zones); })
      .catch((err) => { if (alive) setZonesErr(err.message); });
    return () => { alive = false; };
  }, []);

  const targetCount = parseLines(input).length;

  async function run() {
    setRunning(true);
    setError(null);
    setData(null);
    setExpanded(new Set());
    setNotice(null);
    try {
      const resp = await api('/api/tools/blacklist-check', {
        method: 'POST',
        body: { input, resolver: resolver.trim() || undefined },
      });
      setData(resp);
    } catch (err) {
      setError(err.body?.detail?.join?.(', ') ?? err.body?.detail ?? err.message);
    } finally {
      setRunning(false);
    }
  }

  async function addMailIps(domain) {
    setNotice(`Resolving mail IPs for ${domain}…`);
    try {
      const r = await api(`/api/tools/resolve-mail-ips?domain=${encodeURIComponent(domain)}`);
      if (!r.ips.length) { setNotice(`No A/MX IPs found for ${domain}.`); return; }
      setInput((v) => `${v}\n${r.ips.join('\n')}`);
      setNotice(`Added ${r.ips.length} IP(s) for ${domain} to the input — click "Check" to include them.`);
    } catch (err) {
      setNotice(`Could not resolve ${domain}: ${err.message}`);
    }
  }

  function toggleRow(t) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  }

  function exportCsv() {
    if (!data || !zones) return;
    const cols = ['target', 'kind', 'listed', 'applicable', ...zones.map((z) => z.name)];
    const rows = data.results.map((r) => {
      const cells = [r.target, r.kind, r.summary.listed, r.summary.applicable];
      for (const z of zones) {
        const c = r.cells[z.id];
        if (!c) { cells.push(''); continue; }
        if (c.state === 'listed') cells.push(`LISTED${c.codes?.length ? ` (${c.codes.map((x) => x.meaning).join('; ')})` : ''}`);
        else cells.push(c.state);
      }
      return cells;
    });
    downloadText('blacklist-check.csv', [cols, ...rows].map((row) => row.map(csvCell).join(',')).join('\n'), 'text/csv');
  }

  async function copyListed() {
    if (!data) return;
    const listed = data.results.filter((r) => r.summary.listed > 0).map((r) => r.target);
    if (!listed.length) { setNotice('No listed targets to copy.'); return; }
    try {
      await navigator.clipboard.writeText(listed.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  }

  const anyBlocked = data?.results.some((r) => zones?.some((z) => r.cells[z.id]?.state === 'blocked'));

  return (
    <div className={cardCls}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-t2">
          Check IPs &amp; domains against well-known DNSBL/RBL blocklists — resolves from the panel server.
        </p>
        <FilePicker onText={(t) => setInput((v) => (v ? `${v}\n${t}` : t))} />
      </div>

      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={'1.2.3.4\nmail.example.com\nexample.com\nhttps://foo.com/page\n8.8.8.0/30'}
        className={textareaCls}
      />
      <p className="text-xs text-t3">
        Mix IPs, small CIDRs, domains &amp; URLs (host extracted). Deduped, max {data?.max ?? 50} targets / request.
      </p>

      <div>
        <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-xs text-t3 hover:text-t1 inline-flex items-center gap-1">
          {showAdvanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Resolver setting
        </button>
        {showAdvanced && (
          <label className="block mt-2 max-w-md">
            <span className="text-xs text-t2">Dedicated resolver IP (optional) — results depend on the resolver; some zones refuse public ones like 8.8.8.8</span>
            <input
              type="text" value={resolver} onChange={(e) => setResolver(e.target.value)}
              placeholder="leave blank to use the server's system resolver"
              className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm font-mono"
            />
          </label>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" disabled={running || targetCount === 0 || !zones} onClick={run} className={btnPrimary}>
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
          Check {targetCount} target{targetCount === 1 ? '' : 's'}
        </button>
        {zones && <span className="text-xs text-t3">{zones.length} blocklists</span>}
        {zonesErr && <span className="text-xs text-err">couldn't load blocklists: {zonesErr}</span>}
      </div>

      {error && <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-3 py-2">{error}</div>}
      {notice && <div className="bg-s-2 border border-bd text-t2 text-sm rounded-md px-3 py-2">{notice}</div>}

      {data && zones && (
        <div className="space-y-3">
          {data.skipped?.length > 0 && (
            <p className="text-xs text-warn">skipped (not an IP/domain, or CIDR too large): {data.skipped.join(', ')}</p>
          )}
          {data.capped && <p className="text-xs text-warn">input capped at {data.max} targets — some were dropped.</p>}

          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={exportCsv} className={btnGhost}><Download className="w-4 h-4" /> Export as CSV</button>
            <button type="button" onClick={copyListed} className={btnGhost}><Copy className="w-4 h-4" /> {copied ? 'Copied!' : 'Copy listed targets'}</button>
            <span className="text-xs text-t3">
              resolver: <span className="font-mono">{data.resolver || 'system'}</span>
            </span>
          </div>

          <BlacklistLegend />
          {anyBlocked && (
            <p className="text-xs text-warn">
              ⊘ = the resolver's query was refused/rate-limited (e.g. Spamhaus/Barracuda from a public resolver) — this is NOT "clean".
              Use a dedicated resolver or the provider's DQS for authoritative results.
            </p>
          )}

          <div className="overflow-x-auto border border-bd rounded-md">
            <table className="text-sm">
              <thead>
                <tr className="text-left text-xs text-t3 border-b border-bd">
                  <th className="px-2 py-2 w-6" />
                  <th className="px-3 py-2 sticky left-0 bg-white">Target</th>
                  <th className="px-3 py-2 whitespace-nowrap">Listed</th>
                  {zones.map((z) => (
                    <th key={z.id} className="px-2 py-2 text-center">
                      <span
                        className="cursor-help"
                        title={`${z.name}\nzone: ${z.zone}\ntype: ${z.type}${z.requiresKey ? '\n⚠ requires registration/key' : ''}${z.note ? `\n\n${z.note}` : ''}`}
                      >
                        {z.name}{z.requiresKey ? ' *' : ''}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => {
                  const isOpen = expanded.has(r.target);
                  const listedCells = zones.filter((z) => r.cells[z.id]?.state === 'listed');
                  return (
                    <Fragment key={r.target}>
                      <tr className="border-b border-bd">
                        <td className="px-2 py-1.5">
                          <button type="button" onClick={() => toggleRow(r.target)} className="text-t3 hover:text-t2" title="Details">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs sticky left-0 bg-white">
                          {r.target}
                          <span className="ml-1 text-[10px] uppercase text-t3">{r.kind}</span>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <span className={r.summary.listed > 0 ? 'text-err font-medium' : 'text-ok'}>
                            {r.summary.listed} of {r.summary.applicable}
                          </span>
                        </td>
                        {zones.map((z) => {
                          const c = r.cells[z.id];
                          const st = BL_STATE[c?.state] ?? BL_STATE.error;
                          const tip = c?.state === 'listed'
                            ? `LISTED on ${z.name}${c.codes?.length ? `\n${c.codes.map((x) => `${x.answer} → ${x.meaning}`).join('\n')}` : ''}${c.reason ? `\n${c.reason}` : ''}`
                            : `${z.name}: ${st.label}${c?.detail ? `\n${c.detail}` : ''}`;
                          return (
                            <td key={z.id} className="px-2 py-1.5 text-center">
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded border text-xs cursor-help ${st.cls}`} title={tip}>
                                {st.glyph}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-bd bg-s-2">
                          <td colSpan={3 + zones.length} className="px-4 py-3 text-xs space-y-2">
                            {listedCells.length === 0
                              ? <span className="text-ok">Not listed on any applicable blocklist.</span>
                              : listedCells.map((z) => {
                                  const c = r.cells[z.id];
                                  return (
                                    <div key={z.id}>
                                      <span className="font-semibold text-err">{z.name}</span>
                                      {c.codes?.length > 0 && <span className="text-t2"> — {c.codes.map((x) => x.meaning).join(', ')}</span>}
                                      {c.reason && <div className="ml-3 text-t3 break-all">{c.reason}</div>}
                                      {z.delist && <a href={z.delist} target="_blank" rel="noreferrer" className="ml-3 text-blue hover:underline">delist →</a>}
                                    </div>
                                  );
                                })}
                            {r.kind === 'domain' && (
                              <button type="button" onClick={() => addMailIps(r.target)} className="text-blue hover:underline">
                                + check this domain's A/MX IPs
                              </button>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function BlacklistLegend() {
  return (
    <div className="flex items-center gap-3 flex-wrap text-xs text-t3">
      {['clean', 'listed', 'blocked', 'error', 'not_applicable'].map((s) => (
        <span key={s} className="inline-flex items-center gap-1">
          <span className={`inline-flex items-center justify-center w-4 h-4 rounded border text-[10px] ${BL_STATE[s].cls}`}>{BL_STATE[s].glyph}</span>
          {BL_STATE[s].label}
        </span>
      ))}
    </div>
  );
}

// ─── 2) Email List Cleaner (pure client-side) ───────────────────────────────

// Two doors, same rules (emailCleanerCore.js):
// - paste: small lists, cleaned inline on the main thread;
// - files: any size, streamed + external-sorted in a Web Worker via OPFS
//   (emailCleaner.worker.js). Big files never touch the textarea — pasting
//   300 MB into a DOM node is what froze the tab.

function fmtBytes(n) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

const fmtN = (n) => (n ?? 0).toLocaleString('en-US');

function CountGrid({ counts }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
      {[
        ['input', counts.input, 'text-t2'],
        ['kept', counts.kept, 'text-ok'],
        ['invalid', counts.invalid, 'text-err'],
        ['duplicates', counts.duplicates, 'text-warn'],
        ['blacklisted', counts.blacklisted, 'text-t3'],
      ].map(([label, n, cls]) => (
        <div key={label} className="bg-s-2 border border-bd rounded-md px-2 py-2">
          <div className={`text-lg font-semibold tabular-nums ${cls}`}>{fmtN(n)}</div>
          <div className="text-[11px] uppercase tracking-wide text-t3">{label}</div>
        </div>
      ))}
    </div>
  );
}

function EmailCleaner() {
  const [input, setInput] = useState('');
  const [blacklist, setBlacklist] = useState('');
  const [out, setOut] = useState(null);

  function run() {
    const blocked = parseBlacklist(blacklist);
    const seen = new Set();
    const cleaned = [];
    const counts = { input: 0, kept: 0, invalid: 0, duplicates: 0, blacklisted: 0 };

    for (const item of input.split(SEPARATOR_RE)) {
      const r = classify(item, blocked);
      if (r === DROP_EMPTY) continue;
      counts.input += 1;
      if (r === DROP_INVALID) { counts.invalid += 1; continue; }
      if (r === DROP_BLACKLISTED) { counts.blacklisted += 1; continue; }
      if (seen.has(r)) { counts.duplicates += 1; continue; }
      seen.add(r);
      cleaned.push(r);
    }
    counts.kept = cleaned.length;
    setOut({ cleaned, counts });
  }

  return (
    <div className="space-y-4">
      <div className={cardCls}>
        <p className="text-sm text-t2">
          Trim + lowercase, remove invalid & duplicates, optional domain blacklist.
          <span className="text-t3"> Runs in your browser only — nothing is uploaded.</span>
        </p>
        <label className="block">
          <span className="text-xs font-medium text-t2">Domain blacklist (optional — one per line or comma-separated, applies to both below)</span>
          <input
            type="text"
            value={blacklist}
            onChange={(e) => setBlacklist(e.target.value)}
            placeholder="tempmail.com, mailinator.com"
            className="mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm font-mono"
          />
        </label>
      </div>

      <EmailFileCleaner blacklist={blacklist} />

      <div className={cardCls}>
        <p className="text-sm font-medium text-t1">Paste a short list</p>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} placeholder={'user@example.com\nother@mail.com'} className={textareaCls} />
        <div className="flex items-center gap-2">
          <button type="button" disabled={!input.trim()} onClick={run} className={btnPrimary}>
            <AtSign className="w-4 h-4" /> Clean list
          </button>
          {out && (
            <button
              type="button"
              onClick={() => downloadText('emails-cleaned.txt', out.cleaned.join('\n'))}
              className={btnGhost}
            >
              <Download className="w-4 h-4" /> Download {fmtN(out.cleaned.length)} emails
            </button>
          )}
        </div>
        {out && <CountGrid counts={out.counts} />}
      </div>
    </div>
  );
}

const fileKey = (f) => `${f.name}|${f.size}|${f.lastModified}`;

// Drop zone + picked-file list. One per list: merge mode has one, subtract two.
function FileDrop({ files, setFiles, running, label }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const totalBytes = files.reduce((n, f) => n + f.size, 0);

  function addFiles(list) {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    setFiles((prev) => {
      const have = new Set(prev.map(fileKey));
      return [...prev, ...picked.filter((f) => !have.has(fileKey(f)))];
    });
  }

  return (
    <>
      <div
        onDragOver={(e) => { e.preventDefault(); if (!running) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!running) addFiles(e.dataTransfer.files); }}
        className={`rounded-md border-2 border-dashed px-4 py-5 text-center text-sm ${
          dragOver ? 'border-blue bg-blue/5 text-blue' : 'border-bd-strong text-t3'
        }`}
      >
        {label && <span className="block text-xs font-medium text-t2 mb-1">{label}</span>}
        Drop files here, or{' '}
        <button type="button" disabled={running} onClick={() => inputRef.current?.click()} className="text-blue font-medium hover:underline disabled:opacity-50">
          choose files
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".txt,.csv,text/plain,text/csv"
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {files.length > 0 && (
        <div className="text-sm">
          <ul className="divide-y divide-bd border border-bd rounded-md">
            {files.map((f, i) => (
              <li key={fileKey(f)} className="flex items-center justify-between px-3 py-1.5">
                <span className="font-mono text-xs truncate">{f.name}</span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-t3 tabular-nums">{fmtBytes(f.size)}</span>
                  <button
                    type="button"
                    disabled={running}
                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="text-xs text-t3 hover:text-err disabled:opacity-50"
                  >
                    remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-t3 mt-1">{files.length} file{files.length > 1 ? 's' : ''} · {fmtBytes(totalBytes)} total</p>
        </div>
      )}
    </>
  );
}

function SubtractCounts({ counts, aCounts }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-center">
        {[
          ['removed (in A)', counts.removed, 'text-blue'],
          ['kept', counts.kept, 'text-ok'],
          ['input B', counts.input, 'text-t2'],
          ['invalid', counts.invalid, 'text-err'],
          ['duplicates', counts.duplicates, 'text-warn'],
          ['blacklisted', counts.blacklisted, 'text-t3'],
        ].map(([label, n, cls]) => (
          <div key={label} className="bg-s-2 border border-bd rounded-md px-2 py-2">
            <div className={`text-lg font-semibold tabular-nums ${cls}`}>{fmtN(n)}</div>
            <div className="text-[11px] uppercase tracking-wide text-t3">{label}</div>
          </div>
        ))}
      </div>
      {aCounts && (
        <p className="text-xs text-t3 tabular-nums" data-acounts="">
          List A: {fmtN(aCounts.valid)} valid addresses ({fmtN(aCounts.distinct)} distinct) · {fmtN(aCounts.invalid)} invalid ignored.
        </p>
      )}
    </div>
  );
}

function EmailFileCleaner({ blacklist }) {
  const workerRef = useRef(null);
  const [mode, setMode] = useState('merge');   // 'merge' | 'subtract' (B − A)
  const [files, setFiles] = useState([]);      // merge: all files · subtract: B
  const [filesA, setFilesA] = useState([]);    // subtract: A (addresses to remove)
  const [job, setJob] = useState(null);   // { status: 'running'|'done'|'error'|'cancelled', ... }
  const [download, setDownload] = useState(null);   // { url, bytes }

  const subtract = mode === 'subtract';
  const bBytes = files.reduce((n, f) => n + f.size, 0);
  const totalBytes = bBytes + (subtract ? filesA.reduce((n, f) => n + f.size, 0) : 0);
  const running = job?.status === 'running';
  const ready = files.length > 0 && (!subtract || filesA.length > 0);

  // Stop a running worker when the tab/component goes away.
  useEffect(() => () => workerRef.current?.terminate(), []);
  // Free the previous output's object URL when it is replaced or unmounted.
  useEffect(() => () => { if (download) URL.revokeObjectURL(download.url); }, [download]);

  function switchMode(m) {
    if (running || m === mode) return;
    setMode(m);
    setJob(null);
    setDownload(null);
  }

  function start() {
    if (!ready || running) return;
    setDownload(null);
    if (typeof Worker === 'undefined' || !navigator.storage?.getDirectory) {
      setJob({ status: 'error', message: 'This browser lacks Web Workers or OPFS storage — use a current Chrome, Edge, Firefox or Safari.' });
      return;
    }
    const w = new Worker(new URL('./emailCleaner.worker.js', import.meta.url), { type: 'module' });
    workerRef.current = w;
    setJob({ status: 'running', mode, phase: 'read', bytesRead: 0, totalBytes, counts: {} });
    w.onmessage = (e) => {
      const m = e.data;
      if (m.type === 'progress') {
        setJob((j) => ({ ...j, ...m, status: 'running' }));
      } else if (m.type === 'done') {
        setDownload({ url: URL.createObjectURL(m.file), bytes: m.outBytes });
        setJob({ ...m, status: 'done' });
        w.terminate();
        workerRef.current = null;
      } else if (m.type === 'error') {
        setJob((j) => ({ ...j, status: 'error', message: m.message }));
        w.terminate();
        workerRef.current = null;
      }
    };
    w.onerror = (e) => {
      setJob((j) => ({ ...j, status: 'error', message: e.message || 'Worker failed to start' }));
      w.terminate();
      workerRef.current = null;
    };
    w.postMessage(subtract ? { type: 'start', files, subtract: filesA, blacklist } : { type: 'start', files, blacklist });
  }

  function cancel() {
    workerRef.current?.terminate();
    workerRef.current = null;
    setJob((j) => ({ ...j, status: 'cancelled' }));
  }

  const pct = job
    ? job.phase === 'merge' || job.phase === 'drainA'
      ? job.mode === 'subtract'
        ? 50 + 50 * ((job.bytesRead || 0) / Math.max(1, job.totalBytes || 1))
        : 50 + 50 * ((job.mergeDone || 0) / Math.max(1, job.mergeTotal || 1))
      : 50 * ((job.bytesRead || 0) / Math.max(1, job.totalBytes || 1))
    : 0;
  const lowSpace = job?.free != null && job.free < bBytes * 2.2;
  const outName = job?.mode === 'subtract' ? 'emails-minus-A.txt' : 'emails-cleaned.txt';

  function progressText() {
    if (job.status === 'cancelled') return 'Cancelled.';
    if (job.phase === 'sortA') return `List A "${job.sortingA}" is not in this tool's sorted order — sorting it first`;
    if (job.phase === 'drainA') return 'Checking the rest of list A';
    if (job.phase === 'merge') {
      return job.mode === 'subtract'
        ? `Removing A from ${job.runs} sorted chunk${job.runs === 1 ? '' : 's'} of B — ${fmtN(job.mergeDone)} / ${fmtN(job.mergeTotal)} addresses`
        : `Merging ${job.runs} sorted chunk${job.runs === 1 ? '' : 's'} — ${fmtN(job.mergeDone)} / ${fmtN(job.mergeTotal)} addresses`;
    }
    return job.mode === 'subtract'
      ? `Reading list B ${fmtBytes(job.bytesRead)} / ${fmtBytes(bBytes)}`
      : `Reading ${fmtBytes(job.bytesRead)} / ${fmtBytes(job.totalBytes)}`;
  }

  const tabCls = (m) => `text-xs px-2.5 py-1 rounded-md border ${
    mode === m ? 'border-blue bg-blue/5 text-blue font-medium' : 'border-bd-strong text-t2 hover:bg-s-2'
  } disabled:opacity-50`;

  return (
    <div className={cardCls}>
      <div>
        <p className="text-sm font-medium text-t1">Clean files — any size</p>
        <p className="text-xs text-t3">
          {subtract
            ? 'List B is deduplicated and every address that is also in list A is removed. Output: B minus A, sorted A–Z.'
            : 'Select one or more .txt/.csv files. They are merged, deduplicated and saved as one file.'}
          {' '}Addresses are split on new lines, commas and semicolons.
        </p>
        <div className="flex gap-2 mt-2">
          <button type="button" disabled={running} onClick={() => switchMode('merge')} className={tabCls('merge')}>Merge &amp; dedupe</button>
          <button type="button" disabled={running} onClick={() => switchMode('subtract')} className={tabCls('subtract')}>Remove list A from list B</button>
        </div>
      </div>

      {subtract ? (
        <>
          <FileDrop files={filesA} setFiles={setFilesA} running={running} label="List A — addresses to remove (bounces, unsubscribes…)" />
          <p className="text-xs text-t3 -mt-2">
            Tip for a big A that rarely changes: run it once through <em>Merge &amp; dedupe</em> and keep that file.
            A file already in this tool's sorted order is streamed straight in — no re-sort, no extra storage.
            Any other file is checked and sorted automatically.
          </p>
          <FileDrop files={files} setFiles={setFiles} running={running} label="List B — the list to clean" />
        </>
      ) : (
        <FileDrop files={files} setFiles={setFiles} running={running} />
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" disabled={!ready || running} onClick={start} className={btnPrimary}>
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <AtSign className="w-4 h-4" />}{' '}
          {subtract ? 'Remove A from B' : `Clean ${files.length > 1 ? `${files.length} files` : 'file'}`}
        </button>
        {running && <button type="button" onClick={cancel} className={btnGhost}>Cancel</button>}
        {!running && (files.length > 0 || (subtract && filesA.length > 0)) && (
          <button type="button" onClick={() => { setFiles([]); setFilesA([]); setJob(null); }} className={btnGhost}>Clear files</button>
        )}
        {download && job?.status === 'done' && (
          <a href={download.url} download={outName} className={btnGhost}>
            <Download className="w-4 h-4" /> Download {fmtN(job.counts.kept)} emails ({fmtBytes(download.bytes)})
          </a>
        )}
      </div>

      {job && (
        <div className="space-y-2">
          {(running || job.status === 'cancelled') && (
            <>
              <div className="h-2 rounded-full bg-s-2 overflow-hidden">
                <div className="h-full bg-blue transition-[width]" style={{ width: `${pct.toFixed(1)}%` }} />
              </div>
              <p className="text-xs text-t3 tabular-nums">
                {progressText()}
                {job.elapsedMs != null && ` · ${(job.elapsedMs / 1000).toFixed(1)} s`}
              </p>
            </>
          )}
          {lowSpace && running && (
            <p className="text-xs text-warn">
              Browser storage reports {fmtBytes(job.free)} free; this run needs roughly {fmtBytes(bBytes * 2)}. It may fail with "storage full".
            </p>
          )}
          {job.status === 'error' && <p className="text-sm text-err">Failed: {job.message}</p>}
          {job.status === 'done' && (
            <p className="text-xs text-t3 tabular-nums">
              Done in {(job.elapsedMs / 1000).toFixed(1)} s · {fmtBytes(job.bytesRead)} read · output {fmtBytes(job.outBytes)}.
              {' '}{job.mode === 'subtract'
                ? 'Kept + removed + invalid + duplicates + blacklisted = input B.'
                : 'Kept + invalid + duplicates + blacklisted = input.'}
            </p>
          )}
          {job.status === 'done' && job.mode === 'subtract' && job.aFiles?.some((f) => !f.sorted) && (
            <ul className="text-xs text-warn space-y-0.5" data-afiles="">
              {job.aFiles.filter((f) => !f.sorted).map((f) => (
                <li key={f.name}>
                  List A "{f.name}" was not in sorted order (entry {fmtN(f.outOfOrderAt)}) — sorted it first ({(f.sortMs / 1000).toFixed(1)} s).
                  Merge it once and reuse that file to skip this.
                </li>
              ))}
            </ul>
          )}
          {job.counts && job.status !== 'error' && (job.mode === 'subtract'
            ? <SubtractCounts counts={job.counts} aCounts={job.aCounts} />
            : <CountGrid counts={job.counts} />)}
        </div>
      )}
    </div>
  );
}

// ─── 3) File Splitter (pure client-side, zip download) ──────────────────────

function FileSplitter() {
  const [text, setText] = useState('');
  const [filename, setFilename] = useState('input.txt');
  const [mode, setMode] = useState('parts');   // 'parts' | 'lines'
  const [value, setValue] = useState('2');
  const [keepHeader, setKeepHeader] = useState(false);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  const lines = useMemo(() => (text ? text.split(/\r?\n/) : []), [text]);
  const n = Math.max(1, parseInt(value, 10) || 1);

  async function run() {
    setBusy(true);
    setSummary(null);
    try {
      let body = lines;
      let header = null;
      if (keepHeader && body.length > 1) {
        header = body[0];
        body = body.slice(1);
      }
      // drop a single trailing empty line (common from editors)
      if (body.length && body[body.length - 1] === '') body = body.slice(0, -1);

      const chunkSize = mode === 'parts' ? Math.ceil(body.length / n) : n;
      const chunks = [];
      for (let i = 0; i < body.length; i += chunkSize) {
        chunks.push(body.slice(i, i + chunkSize));
      }

      const zip = new JSZip();
      const base = filename.replace(/\.[^.]+$/, '') || 'part';
      const ext = filename.includes('.') ? filename.slice(filename.lastIndexOf('.')) : '.txt';
      chunks.forEach((chunk, i) => {
        const content = (header != null ? [header, ...chunk] : chunk).join('\n');
        zip.file(`${base}-part${i + 1}${ext}`, content);
      });
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${base}-split.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setSummary({ parts: chunks.length, lines: body.length });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={cardCls}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-t2">
          Split a text/CSV file into parts and download them as a zip.
          <span className="text-t3"> Runs in your browser only.</span>
        </p>
        <FilePicker onText={(t, name) => { setText(t); if (name) setFilename(name); }} />
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="paste content or load a file…" className={textareaCls} />
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <span className="text-t3">{lines.length ? `${lines.length} lines` : 'no content'}</span>
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="rounded-md border border-bd-strong px-2 py-1 text-sm bg-white">
          <option value="parts">split into N parts</option>
          <option value="lines">chunks of X lines</option>
        </select>
        <input
          type="number" min="1" value={value} onChange={(e) => setValue(e.target.value)}
          className="w-20 rounded-md border border-bd-strong px-2 py-1 text-sm"
        />
        <label className="inline-flex items-center gap-1">
          <input type="checkbox" checked={keepHeader} onChange={(e) => setKeepHeader(e.target.checked)} />
          <span className="text-xs text-t2">first line is a header (repeat in every part)</span>
        </label>
        <button type="button" disabled={busy || lines.length === 0} onClick={run} className={btnPrimary}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scissors className="w-4 h-4" />}
          Split & download zip
        </button>
      </div>
      {summary && (
        <p className="text-sm text-ok">
          Done — {summary.lines} lines → {summary.parts} part{summary.parts === 1 ? '' : 's'} (zip downloaded).
        </p>
      )}
    </div>
  );
}

// ─── 4) Email Domain Grouper (pure client-side) ─────────────────────────────

function DomainGrouper() {
  const [input, setInput] = useState('');
  const [rows, setRows] = useState(null);
  const [sortDesc, setSortDesc] = useState(true);

  function run() {
    const emails = parseLines(input).map((e) => e.toLowerCase()).filter((e) => EMAIL_RE.test(e));
    const counts = new Map();
    for (const e of emails) {
      const d = e.split('@')[1];
      counts.set(d, (counts.get(d) ?? 0) + 1);
    }
    const total = emails.length;
    setRows({
      total,
      invalid: parseLines(input).length - total,
      list: [...counts.entries()].map(([domain, count]) => ({ domain, count, pct: total ? (count / total) * 100 : 0 })),
    });
  }

  const sorted = rows ? [...rows.list].sort((a, b) => (sortDesc ? b.count - a.count : a.count - b.count)) : [];

  return (
    <div className={cardCls}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-t2">
          Count emails per domain. <span className="text-t3">Runs in your browser only.</span>
        </p>
        <FilePicker onText={(t) => setInput((v) => (v ? `${v}\n${t}` : t))} />
      </div>
      <textarea value={input} onChange={(e) => setInput(e.target.value)} placeholder={'user@gmail.com\nother@yahoo.com'} className={textareaCls} />
      <div className="flex items-center gap-2">
        <button type="button" disabled={!input.trim()} onClick={run} className={btnPrimary}>
          <PieChart className="w-4 h-4" /> Group by domain
        </button>
        {rows && (
          <button
            type="button"
            onClick={() =>
              downloadText(
                'domains.csv',
                `domain,count,percent\n${sorted.map((r) => `${r.domain},${r.count},${r.pct.toFixed(1)}`).join('\n')}`,
                'text/csv',
              )
            }
            className={btnGhost}
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        )}
      </div>
      {rows && (
        <div className="space-y-2">
          <p className="text-xs text-t3">
            {rows.total} valid emails · {rows.list.length} domains{rows.invalid > 0 ? ` · ${rows.invalid} invalid lines skipped` : ''}
          </p>
          <div className="border border-bd rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                  <th className="px-3 py-2"><Globe2 className="w-3.5 h-3.5 inline mr-1" />Domain</th>
                  <th
                    className="px-3 py-2 cursor-pointer select-none"
                    onClick={() => setSortDesc((v) => !v)}
                    title="Click to flip sort"
                  >
                    Count {sortDesc ? '↓' : '↑'}
                  </th>
                  <th className="px-3 py-2">%</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.domain} className="border-b border-bd last:border-0">
                    <td className="px-3 py-1.5 font-mono">{r.domain}</td>
                    <td className="px-3 py-1.5">{r.count}</td>
                    <td className="px-3 py-1.5 text-t3">{r.pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
